import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { User, LoginResponse } from '../types';

interface AuthContextType {
    user: User | null;
    token: string | null;
    initializing: boolean;
    login: (data: LoginResponse) => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    initializing: true,
    login: () => {},
    logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        const stored = localStorage.getItem('auth');
        if (stored) {
            const data = JSON.parse(stored) as LoginResponse;
            setUser(data.user);
            setToken(data.token);
        }
        setInitializing(false);
    }, []);

    useEffect(() => {
        const expireSession = () => {
            setUser(null);
            setToken(null);
            localStorage.removeItem('auth');
        };
        window.addEventListener('admin-auth-expired', expireSession);
        return () => window.removeEventListener('admin-auth-expired', expireSession);
    }, []);

    const login = (data: LoginResponse) => {
        setUser(data.user);
        setToken(data.token);
        localStorage.setItem('auth', JSON.stringify(data));
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth');
    };

    return (
        <AuthContext.Provider value={{ user, token, initializing, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}
